import dayjs from 'dayjs';

const naiveRound = (value, decimalPlaces = 0) => {
    if (!Number.isFinite(Number(value))) {
        return value;
    }

    if (!value) {
        const zero = 0;
        return zero.toFixed(decimalPlaces);
    }

    const p = Math.pow(10, decimalPlaces);
    const ret = Math.round(Number(value) * p) / p;
    return Number(ret.toFixed(decimalPlaces));
};

const formatCurrency = (x, symbol = '$', decimalPlaces = 2) => {
    const rounded = naiveRound(x, decimalPlaces);
    const str = rounded.toString().split('.', 2);
    const whole = str[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return str.length == 1
        ? `${symbol}${whole}`
        : `${symbol}${whole}.${str[1]}`;
};

const countVariations = (text) => {
    const regexp = /{([^{}]+?)}/g;

    if (!text) {
        return 0;
    }

    let arr = [];
    const matches = text.matchAll(regexp);
    for (const match of matches) {
        arr.push(match[0]);
    }

    if (arr.length === 0) {
        return 1;
    }

    const variations = arr
        .map((m) => m.split('|').length)
        .reduce((p, c) => p * c);
    return variations;
};

const nFormatter = (num, digits) => {
    const lookup = [
        { value: 1, symbol: '' },
        { value: 1e3, symbol: 'k' },
        { value: 1e6, symbol: 'M' },
        { value: 1e9, symbol: 'G' },
        { value: 1e12, symbol: 'T' },
        { value: 1e15, symbol: 'P' },
        { value: 1e18, symbol: 'E' },
    ];
    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    const item = lookup
        .slice()
        .reverse()
        .find((item) => num >= item.value);
    return item
        ? (num / item.value).toFixed(digits).replace(rx, '$1') + item.symbol
        : '0';
};

const friendlyElapsedTime = (datetime) => {
    const currentTime = dayjs();
    const compareTime = dayjs(datetime).utc();
    let elapsed = currentTime.diff(compareTime, 'seconds');
    if (elapsed < 60) {
        return 'moments ago';
    } else if (elapsed < 60 * 60) {
        return `${Math.abs(
            compareTime.diff(currentTime, 'minutes')
        )} minutes ago`;
    } else if (elapsed < 60 * 60 * 24) {
        return `${Math.abs(compareTime.diff(currentTime, 'hours'))} hours ago`;
    }
    return `${Math.abs(compareTime.diff(currentTime, 'days'))} days ago`;
};

const friendlyCamelCase = (str) =>
    str ? str.replace(/([A-Z])/g, ' $1').trim() : str;

const capitalize = (s) => (s && s[0].toUpperCase() + s.slice(1)) || '';

/**
 * Converts PascalCase / camelCase text to a human-friendly string.
 * - Inserts spaces before word breaks:
 *     "MyPascalCase"   -> "My Pascal Case"
 *     "parseHTTPError" -> "Parse HTTP Error"
 * - Preserves capitalized acronyms like “HTTP”, “ID”, etc.
 * - Keeps the first letter capitalized (Title-case output).
 *
 * @param {string} input
 * @returns {string}
 */
function pascalToFriendly(input = '') {
    if (!input) return '';

    // 1) "HTMLParser" -> "HTML Parser"  (acronym followed by capital+lowercase)
    // 2) "parseHTTPError" -> "parse HTTP Error" (lowercase→acronym boundary)
    // 3) "myIDNumber" -> "my ID Number" (lowercase→capital boundary)
    const spaced = input
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // (1)
        .replace(/([a-z\d])([A-Z])/g, '$1 $2'); // (2 & 3)

    // Capitalize the very first character, keep rest as-is
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function resolveSpintax(text) {
    const regex = /\{([^{}]+)\}/;

    while (regex.test(text)) {
        text = text.replace(regex, (match, group) => {
            const options = group.split('|');
            return options[Math.floor(Math.random() * options.length)];
        });
    }

    return text;
}

export {
    naiveRound,
    formatCurrency,
    countVariations,
    nFormatter,
    friendlyElapsedTime,
    friendlyCamelCase,
    capitalize,
    pascalToFriendly,
    resolveSpintax,
};
